import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import './AdminDashboard.css';
import {
  FaChartBar, FaUsers, FaCreditCard, FaMoneyBillWave, FaExclamationTriangle,
  FaChartLine, FaUserTie, FaClipboardList, FaBell, FaUser, FaEdit,
  FaCog, FaMobileAlt, FaFileAlt, FaLock, FaSearch, FaRobot, FaShieldAlt,
  FaHeart, FaCheckCircle, FaEllipsisH, FaSignOutAlt, FaDice, FaGamepad, FaCoins, FaBook, FaExchangeAlt, FaRocket,
  FaClipboardCheck, FaComments, FaBan
} from 'react-icons/fa';

// Admin Components
import DashboardStats from '../components/admin/DashboardStats';
import CustomerManagement from '../components/admin/CustomerManagement';
import AccountManagement from '../components/admin/AccountManagement';
import TransactionManagement from '../components/admin/TransactionManagement';
import ComplianceAlerts from '../components/admin/ComplianceAlerts';
import AdminUserManagement from '../components/admin/AdminUserManagement';
import AuditLogs from '../components/admin/AuditLogs';
import IpBlocklist from '../components/admin/IpBlocklist';
import AdminPlatformCoins from '../components/admin/AdminPlatformCoins';
import ReportsAnalytics from '../components/admin/ReportsAnalytics';
import AdminNotifications from '../components/admin/AdminNotifications';
import AdminProfile from '../components/admin/AdminProfile';
import ReviewQueue from '../components/admin/ReviewQueue';
import ProviderConfigManagement from '../components/admin/ProviderConfigManagement';
import TradingProviderSelection from '../components/admin/TradingProviderSelection';
import AssetTokens from '../components/admin/AssetTokens';
import MultiSignatureWallets from '../components/admin/MultiSignatureWallets';
import SocialMediaContentManagement from '../components/admin/SocialMediaContentManagement';
import AdminSettings from './AdminSettings';
import TradingBotsPanel from '../components/admin/TradingBotsPanel';
import CryptoPortfolio from './CryptoPortfolio';
import StockPortfolio from './StockPortfolio';
import OrderBookManagement from '../components/admin/OrderBookManagement';
import MatchingEngineManagement from '../components/admin/MatchingEngineManagement';
import AdminLogo from '../components/admin/AdminLogo';
import RiskManagementPanel from '../components/admin/RiskManagementPanel';
import SystemHealthPanel from '../components/admin/SystemHealthPanel';
import EligibilityManagementPanel from '../components/admin/EligibilityManagementPanel';
import KYTDashboard from '../components/admin/KYTDashboard';
import ProductManagement from '../components/admin/ProductManagement';
import IGamingManagement from '../components/admin/IGamingManagement';
import P2PGamesManagement from '../components/admin/P2PGamesManagement';
import AdminCryptoPayouts from './AdminCryptoPayouts';
import AdminTradingAccounts from '../components/admin/AdminTradingAccounts';
import { KucoinSubaccountProvisionerPanel } from '../components/admin/KucoinSubaccountProvisioning';
import AdminStablecoinManagement from '../components/admin/AdminStablecoinManagement';
import AdminTrueLayerTopUp from '../components/admin/AdminTrueLayerTopUp';
import ClientOnboarding from '../components/admin/ClientOnboarding';
import ClientFeatureSelector from '../components/admin/ClientFeatureSelector';
import AdminMarkets from '../components/admin/AdminMarkets';
import SuperAdminSubscriptions from '../components/admin/SuperAdminSubscriptions';
import SubscriptionCryptoPaymentApprovals from '../components/admin/SubscriptionCryptoPaymentApprovals';
import AdminSubscriptionCryptoPaymentApprovals from '../components/admin/AdminSubscriptionCryptoPaymentApprovals';
import AdminLandingPageManager from '../components/admin/AdminLandingPageManager';
import AdminFaqManager from '../components/admin/AdminFaqManager';
import AdminCreatorChatModalBuilder from '../components/admin/AdminCreatorChatModalBuilder';
import AdminLoginDisplaySettings from '../components/admin/AdminLoginDisplaySettings';
import MarketLanding from './MarketLanding';

// Admin Services
import { adminAuthService } from '../services/adminAuthService';
import { adminApiService } from '../services/adminApiService';
import { apiService } from '../services/apiService';
import QRCode from 'qrcode';
import type { AdminUser } from '../types/admin';
import { useAdminApiService } from '../hooks/useAdminApiService';
import { setAdminUser } from '../store/slices/authSlice';

// Move getIsMobile outside component to prevent recreation
const getIsMobile = () => window.matchMedia('(max-width: 768px)').matches;
const normalizeChain = (raw?: string) => {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'eth') return 'ethereum';
  if (v === 'matic') return 'polygon';
  if (v === 'btc') return 'bitcoin';
  if (v === 'xlm') return 'stellar';
  if (v === 'sol') return 'solana';
  if (v === 'ada') return 'cardano';
  return v;
};

type AdminPlanPaymentSetting = 'ALL' | 'STRIPE' | 'CRYPTO';

const parseAdminPlanPaymentMethods = (raw: unknown): AdminPlanPaymentSetting => {
  const u = String(raw ?? 'ALL').trim().toUpperCase();
  if (u === 'STRIPE' || u === 'CRYPTO' || u === 'ALL') return u;
  return 'ALL';
};

const allowedAdminRenewPaymentMethods = (pm: AdminPlanPaymentSetting): Array<'STRIPE' | 'CRYPTO'> => {
  if (pm === 'STRIPE') return ['STRIPE'];
  if (pm === 'CRYPTO') return ['CRYPTO'];
  return ['STRIPE', 'CRYPTO'];
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL params first, then localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromUrl = urlParams.get('tab');
    return tabFromUrl || localStorage.getItem('adminActiveTab') || 'dashboard';
  });
  
  // Use Redux state instead of local state
  const adminUser = useAppSelector(state => state.auth.adminUser) as AdminUser | null;
  const authRefreshing = useAppSelector(state => state.auth.isLoading);
  const cachedAdminPermissionsRef = useRef<string[]>([]);
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [adminSubExpiredMessage, setAdminSubExpiredMessage] = useState<string | null>(null);
  const [adminSubscriptionStatus, setAdminSubscriptionStatus] = useState<any>(null);
  const [pendingAdminRenewal, setPendingAdminRenewal] = useState<any>(null);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [renewPaymentMethod, setRenewPaymentMethod] = useState<'STRIPE' | 'CRYPTO'>('STRIPE');
  const [renewCryptoBlockchain, setRenewCryptoBlockchain] = useState('ethereum');
  const [renewCryptoSymbol, setRenewCryptoSymbol] = useState('USDT');
  const [renewTxHash, setRenewTxHash] = useState('');
  const [renewTxBusy, setRenewTxBusy] = useState(false);
  const [renewWalletBusy, setRenewWalletBusy] = useState(false);
  const [renewDestinationQr, setRenewDestinationQr] = useState<string | null>(null);
  const [renewOwnWalletAddress, setRenewOwnWalletAddress] = useState<string | null>(null);
  const [renewOwnWalletQr, setRenewOwnWalletQr] = useState<string | null>(null);
  const [myStrategyCount, setMyStrategyCount] = useState<number | null>(null);
  const [enrolledUserCount, setEnrolledUserCount] = useState<number | null>(null);
  const adminApi = useAdminApiService();
  const sidebarNavRef = React.useRef<HTMLElement | null>(null);

  // After soft-nav from login (esp. mobile keyboard), dismiss focus and scroll so
  // 100dvh / fixed chrome recalculate against the full visual viewport.
  useEffect(() => {
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }
    window.scrollTo(0, 0);
    const vv = window.visualViewport;
    if (!vv) return;
    const onViewportChange = () => {
      // Touch layout after URL bar / keyboard settle; no-op read forces recalc on some WebKits
      void document.documentElement.clientHeight;
    };
    vv.addEventListener('resize', onViewportChange);
    vv.addEventListener('scroll', onViewportChange);
    const t = window.setTimeout(onViewportChange, 300);
    return () => {
      vv.removeEventListener('resize', onViewportChange);
      vv.removeEventListener('scroll', onViewportChange);
      window.clearTimeout(t);
    };
  }, []);

  // Listen for admin-subscription-expired events fired by Axios interceptor
  useEffect(() => {
    const handleAdminSubExpired = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setAdminSubExpiredMessage(
        detail?.message || 'Your admin subscription has expired. Please contact the platform owner to renew access.'
      );
    };
    window.addEventListener('admin-subscription-expired', handleAdminSubExpired);
    return () => window.removeEventListener('admin-subscription-expired', handleAdminSubExpired);
  }, []);

  // Fetch the current admin's own subscription status and strategy count on mount
  useEffect(() => {
    const fetchAdminSubStatus = async () => {
      try {
        const [subResponse, strategiesResponse, enrolledResponse, renewPendingResponse] = await Promise.all([
          adminApi.getAdminSubscriptionStatus(),
          apiService.get('/api/admin/trading-strategies'),
          apiService.get('/api/admin/trading-strategies/enrolled-users/count'),
          adminApi.getAdminSubscriptionRenewPending(),
        ]);
        if (subResponse.success && subResponse.data) {
          setAdminSubscriptionStatus(subResponse.data);
        }
        if (renewPendingResponse.success && renewPendingResponse.data) {
          setPendingAdminRenewal(renewPendingResponse.data);
        }
        if (strategiesResponse.success && Array.isArray(strategiesResponse.data)) {
          setMyStrategyCount(strategiesResponse.data.length);
        }
        if (enrolledResponse.success && enrolledResponse.data) {
          setEnrolledUserCount(enrolledResponse.data.enrolledUsers ?? 0);
        }
      } catch (err) {
        console.error('Failed to fetch admin subscription status:', err);
      }
    };
    fetchAdminSubStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPendingAdminRenewal = useCallback(async () => {
    try {
      const renewPendingResponse = await adminApi.getAdminSubscriptionRenewPending();
      if (renewPendingResponse.success && renewPendingResponse.data) {
        setPendingAdminRenewal(renewPendingResponse.data);
      }
    } catch {
      /* non-blocking */
    }
  }, [adminApi]);

  const adminRenewEligible = useMemo(() => {
    if (!adminSubscriptionStatus || adminUser?.role === 'SUPER_ADMIN') return false;
    const st = adminSubscriptionStatus.status;
    const pid = adminSubscriptionStatus.planId;
    if (pid == null || pid === '') return false;
    if (pendingAdminRenewal?.hasPending) return false;
    if (adminSubscriptionStatus.hasAccess === true) return false;
    return st === 'EXPIRED' || st === 'SUSPENDED' || st === 'CANCELLED';
  }, [adminSubscriptionStatus, adminUser?.role, pendingAdminRenewal]);

  const adminRenewPaymentMethodsSetting = useMemo(
    () =>
      parseAdminPlanPaymentMethods(
        adminSubscriptionStatus?.registrationPaymentMethods ??
          adminSubscriptionStatus?.registration_payment_methods
      ),
    [adminSubscriptionStatus]
  );
  const adminRenewAllowedPaymentMethods = useMemo((): Array<'STRIPE' | 'CRYPTO'> => {
    return allowedAdminRenewPaymentMethods(adminRenewPaymentMethodsSetting);
  }, [adminRenewPaymentMethodsSetting]);

  useEffect(() => {
    if (!renewModalOpen || !adminRenewEligible) return;
    if (!adminRenewAllowedPaymentMethods.includes(renewPaymentMethod)) {
      setRenewPaymentMethod(adminRenewAllowedPaymentMethods[0]);
    }
  }, [renewModalOpen, adminRenewEligible, adminRenewAllowedPaymentMethods, renewPaymentMethod]);

  const startAdminRenewalFlow = async (
    opts?: { paymentMethod?: 'STRIPE' | 'CRYPTO'; cryptoBlockchain?: string; cryptoSymbol?: string }
  ) => {
    const pid = adminSubscriptionStatus?.planId;
    if (pid == null || renewBusy || adminUser?.role === 'SUPER_ADMIN') return;
    setRenewBusy(true);
    setRenewError(null);
    try {
      const paymentMethod = opts?.paymentMethod || 'STRIPE';
      const res = await adminApi.startAdminSubscriptionRenewal(
        Number(pid),
        paymentMethod,
        opts?.cryptoBlockchain,
        opts?.cryptoSymbol
      );
      if (!res.success) {
        setRenewError((res as any).error || 'Renewal could not be started.');
        return;
      }
      const data = res.data as any;
      if (data?.requiresCompliance) {
        setRenewModalOpen(false);
        await loadPendingAdminRenewal();
        const subRes = await adminApi.getAdminSubscriptionStatus();
        if (subRes.success && subRes.data) setAdminSubscriptionStatus(subRes.data);
      } else if (data?.checkoutUrl) {
        setRenewModalOpen(false);
        window.open(data.checkoutUrl as string, '_blank', 'noopener,noreferrer');
        await loadPendingAdminRenewal();
        const subRes = await adminApi.getAdminSubscriptionStatus();
        if (subRes.success && subRes.data) setAdminSubscriptionStatus(subRes.data);
      } else if (String(data?.paymentMethod || '').toUpperCase() === 'CRYPTO') {
        setRenewModalOpen(false);
        await loadPendingAdminRenewal();
      }
    } catch (err: any) {
      setRenewError(err?.message || 'Renewal could not be started.');
    } finally {
      setRenewBusy(false);
    }
  };

  const submitAdminRenewalCryptoProof = async () => {
    if (!pendingAdminRenewal?.renewalRequestId || !renewTxHash.trim()) return;
    setRenewTxBusy(true);
    setRenewError(null);
    try {
      const res = await adminApi.submitAdminSubscriptionCryptoPayment({
        renewalRequestId: Number(pendingAdminRenewal.renewalRequestId),
        txHash: renewTxHash.trim(),
      });
      if (!res.success) {
        setRenewError((res as any).error || 'Could not submit transaction hash.');
        return;
      }
      setRenewTxHash('');
      await loadPendingAdminRenewal();
    } catch (e: any) {
      setRenewError(e?.message || 'Could not submit transaction hash.');
    } finally {
      setRenewTxBusy(false);
    }
  };

  const payAdminRenewalWithWallet = async () => {
    if (!pendingAdminRenewal?.renewalRequestId) return;
    setRenewWalletBusy(true);
    setRenewError(null);
    try {
      const res = await adminApi.payAdminSubscriptionWithWallet({
        renewalRequestId: Number(pendingAdminRenewal.renewalRequestId),
      });
      if (!res.success) {
        setRenewError((res as any).error || 'Wallet payment failed.');
        return;
      }
      await loadPendingAdminRenewal();
    } catch (e: any) {
      setRenewError(e?.message || 'Wallet payment failed.');
    } finally {
      setRenewWalletBusy(false);
    }
  };

  useEffect(() => {
    let canceled = false;
    const loadRenewalWalletVisuals = async () => {
      const p = pendingAdminRenewal;
      if (
        !p?.hasPending ||
        p.status !== 'AWAITING_PAYMENT' ||
        String(p.paymentMethod || '').toUpperCase() !== 'CRYPTO'
      ) {
        setRenewDestinationQr(null);
        setRenewOwnWalletAddress(null);
        setRenewOwnWalletQr(null);
        return;
      }
      const destinationAddress = String(p.destinationAddress || p.cryptoDestinationAddress || '').trim();
      const chain = normalizeChain(String(p.blockchain || p.cryptoBlockchain || ''));
      try {
        if (destinationAddress) {
          const dqr = await QRCode.toDataURL(destinationAddress, { width: 160, margin: 2, errorCorrectionLevel: 'M' });
          if (!canceled) setRenewDestinationQr(dqr);
        }
      } catch {
        if (!canceled) setRenewDestinationQr(null);
      }
      try {
        const wallets = await apiService.get<Array<{ blockchain?: string; address?: string }>>('/crypto/wallet');
        if (canceled) return;
        const list = Array.isArray(wallets.data) ? wallets.data : [];
        const found = list.find((w) => normalizeChain(w.blockchain) === chain);
        if (found?.address) {
          setRenewOwnWalletAddress(found.address);
          try {
            const uqr = await QRCode.toDataURL(found.address, { width: 160, margin: 2, errorCorrectionLevel: 'M' });
            if (!canceled) setRenewOwnWalletQr(uqr);
          } catch {
            if (!canceled) setRenewOwnWalletQr(null);
          }
        } else {
          setRenewOwnWalletAddress(null);
          setRenewOwnWalletQr(null);
        }
      } catch {
        if (!canceled) {
          setRenewOwnWalletAddress(null);
          setRenewOwnWalletQr(null);
        }
      }
    };
    void loadRenewalWalletVisuals();
    return () => {
      canceled = true;
    };
  }, [pendingAdminRenewal]);

  useEffect(() => {
    const stripe = (searchParams.get('stripe') || '').toLowerCase();
    const adminRenewal = searchParams.get('adminRenewal');
    if (stripe !== 'success' || adminRenewal !== '1') return;
    if (loading || !adminUser || adminUser.role === 'SUPER_ADMIN') return;

    const sessionId = searchParams.get('session_id');

    void (async () => {
      try {
        const res = await adminApi.confirmAdminSubscriptionRenewalPayment(
          sessionId ? { sessionId } : {}
        );
        if (res.success && res.data) {
          setPendingAdminRenewal(res.data as any);
        } else {
          await loadPendingAdminRenewal();
        }
      } catch (e) {
        console.warn('Admin renewal payment confirm failed (webhook may still apply):', e);
        await loadPendingAdminRenewal();
      }
      try {
        const subRes = await adminApi.getAdminSubscriptionStatus();
        if (subRes.success && subRes.data) setAdminSubscriptionStatus(subRes.data);
      } catch {
        /* ignore */
      }
      const qp = new URLSearchParams();
      const tab = searchParams.get('tab');
      if (tab && tab !== 'dashboard') qp.set('tab', tab);
      const tail = qp.toString() ? `?${qp.toString()}` : '';
      navigate(`/admin/dashboard${tail}`, { replace: true });
    })();
  }, [searchParams, loading, adminUser, adminApi, navigate, loadPendingAdminRenewal]);

  // Add refs to prevent repeated calls
  const authCheckedRef = useRef(false);
  const notificationsLoadedRef = useRef(false);
  const notificationsLoadingRef = useRef(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingSetToFalseRef = useRef(false); // Track if loading was already set to false

  // Add debugging for re-renders
  console.log('AdminDashboard rendering with activeTab:', activeTab);

  // Memoize the resize handler to prevent recreation on every render
  const handleResize = useCallback(() => {
    // Clear any existing timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    
    // Debounce the resize event to prevent excessive updates
    resizeTimeoutRef.current = setTimeout(() => {
      const newIsMobile = getIsMobile();
      setIsMobile(prevIsMobile => {
        // Only update if the value actually changed
        if (prevIsMobile !== newIsMobile) {
          console.log('AdminDashboard: Mobile state changed from', prevIsMobile, 'to', newIsMobile);
          return newIsMobile;
        }
        return prevIsMobile;
      });
    }, 100); // 100ms debounce
  }, []);

  useEffect(() => {
    // Only run auth check once on initial mount
    if (!authCheckedRef.current) {
      console.log('AdminDashboard: Initial auth check (one-time)');
      authCheckedRef.current = true;
      checkAdminAuth();
    } else {
      console.log('AdminDashboard: Auth already checked, skipping');
    }
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      // Clean up timeout on unmount
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      // Don't reset authCheckedRef on unmount - keep it set
    };
  }, [handleResize]); // Removed checkAdminAuth from deps to prevent re-running

  // Add effect to handle Redux state changes (rehydration)
  // Run once to set initial loading state based on Redux
  useEffect(() => {
    console.log('AdminDashboard: Checking Redux state for initial load', { 
      adminUser: !!adminUser, 
      isAuthenticated,
      currentLoadingState: loading,
      alreadySetToFalse: loadingSetToFalseRef.current
    });
    
    // Only update loading once if we have valid auth data
    if (adminUser && isAuthenticated && loading && !loadingSetToFalseRef.current) {
      console.log('AdminDashboard: Valid admin user from Redux, setting loading false');
      loadingSetToFalseRef.current = true;
      setLoading(false);
    }
    // Don't set loading to true here - let checkAdminAuth handle that
  }, [adminUser, isAuthenticated, loading]);

  // Use a ref to track if we're updating from URL to avoid circular updates
  const isUpdatingFromUrlRef = useRef(false);
  const lastSyncedTabRef = useRef<string | null>(null);

  // Sync URL with activeTab state - CRITICAL: This preserves tab state on refresh
  useEffect(() => {
    // Skip if we're currently updating from URL to avoid circular updates
    if (isUpdatingFromUrlRef.current) {
      return;
    }
    
    // Skip if we've already synced this tab to avoid unnecessary navigation
    if (lastSyncedTabRef.current === activeTab) {
      return;
    }
    
    const currentTab = searchParams.get('tab');
    // Only update URL if it's different from current activeTab
    if (currentTab !== activeTab) {
      // Set flag to prevent the URL sync effect from overriding this change
      isUpdatingFromUrlRef.current = true;
      
      const newSearchParams = new URLSearchParams(searchParams);
      if (activeTab && activeTab !== 'dashboard') {
        newSearchParams.set('tab', activeTab);
      } else {
        // Remove tab param if it's dashboard (cleaner URL)
        newSearchParams.delete('tab');
      }
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch ? `/admin/dashboard?${newSearch}` : '/admin/dashboard';
      // Use replace to avoid adding to history on every state change
      navigate(newUrl, { replace: true });
      lastSyncedTabRef.current = activeTab;
      
      // Reset flag after navigation completes
      setTimeout(() => {
        isUpdatingFromUrlRef.current = false;
      }, 100);
    }
  }, [activeTab, navigate, searchParams]);

  // Sync activeTab with URL params - CRITICAL: This preserves tab when URL changes (back/forward, refresh)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    
    // ONLY update if there's a meaningful difference AND we're not currently updating from a user action
    if (tabFromUrl && tabFromUrl !== activeTab && tabFromUrl !== lastSyncedTabRef.current && !isUpdatingFromUrlRef.current) {
      // Update activeTab from URL
      isUpdatingFromUrlRef.current = true;
      console.log('AdminDashboard: Syncing activeTab from URL:', tabFromUrl);
      setActiveTab(tabFromUrl);
      localStorage.setItem('adminActiveTab', tabFromUrl);
      lastSyncedTabRef.current = tabFromUrl;
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromUrlRef.current = false;
      }, 100); // Increased delay to prevent rapid cycles
    } else if (!tabFromUrl && activeTab !== 'dashboard' && activeTab !== lastSyncedTabRef.current && !isUpdatingFromUrlRef.current) {
      // ONLY restore from localStorage if we haven't already processed this state
      const savedTab = localStorage.getItem('adminActiveTab');
      if (savedTab && savedTab !== activeTab && savedTab !== 'dashboard' && savedTab !== lastSyncedTabRef.current) {
        console.log('AdminDashboard: Restoring activeTab from localStorage and updating URL:', savedTab);
        isUpdatingFromUrlRef.current = true;
        setActiveTab(savedTab);
        lastSyncedTabRef.current = savedTab;
        // Update URL to match
        navigate(`/admin/dashboard?tab=${savedTab}`, { replace: true });
        setTimeout(() => {
          isUpdatingFromUrlRef.current = false;
        }, 100); // Increased delay to prevent rapid cycles
      }
    }
  }, [searchParams]); // ONLY depend on searchParams to prevent conflicts with activeTab changes

  // Add debugging for tab changes
  useEffect(() => {
    console.log('AdminDashboard: Active tab changed to:', activeTab);
  }, [activeTab]);

  // Reset sidebar nav scroll position when mobile menu opens
  useEffect(() => {
    if (isMobile && mobileMenuOpen && sidebarNavRef.current) {
      // Reset scroll to top when menu opens - use setTimeout to ensure DOM is ready
      const timer = setTimeout(() => {
        if (sidebarNavRef.current) {
          // Scroll to very top
          sidebarNavRef.current.scrollTop = 0;
          // Force scroll to top by using scrollIntoView on first item
          const firstItem = sidebarNavRef.current.querySelector('.nav-item');
          if (firstItem) {
            firstItem.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
          }
        }
      }, 150); // Wait for slide-in animation to complete
      
      return () => clearTimeout(timer);
    }
  }, [isMobile, mobileMenuOpen]);

  // COMMENTED OUT - These useEffects are redundant with initial state setup
  // The initial state (lines 41-46) already handles URL params and localStorage
  // Removing these to prevent double state updates and double refresh
  
  // // Restore activeTab from localStorage on mount
  // useEffect(() => {
  //   const savedTab = localStorage.getItem('adminActiveTab');
  //   if (savedTab && savedTab !== activeTab) {
  //     setActiveTab(savedTab);
  //   }
  // }, []);

  // // Handle URL parameter for tab (only once on mount to avoid tab-flip loops)
  // useEffect(() => {
  //   const tabParam = new URLSearchParams(window.location.search).get('tab');
  //   if (tabParam && navigationItems.some(item => item.id === tabParam)) {
  //     setActiveTab(tabParam);
  //     localStorage.setItem('adminActiveTab', tabParam);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const loadNotifications = useCallback(async () => {
    // Don't make API calls if user is not authenticated
    const user = adminAuthService.getCurrentAdmin();
    if (!user || !user.username || !user.role) {
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (notificationsLoadedRef.current || notificationsLoadingRef.current) {
      console.log('AdminDashboard: Notifications already loaded or loading, skipping');
      return;
    }
    
    try {
      console.log('AdminDashboard: Making API call to load notifications');
      const alerts = await adminApi.getComplianceAlerts(0, 5, undefined, 'OPEN');
      if (alerts.success && alerts.data) {
        setNotifications(alerts.data.content || []);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, []); // Removed adminApi dependency since it's now stable

  const checkAdminAuth = useCallback(async () => {
    console.log('AdminDashboard: checkAdminAuth START', { adminUser: !!adminUser });
    try {
      // Check Redux state first, then fallback to service
      if (!adminUser || !adminUser.username || !adminUser.role) {
        console.log('AdminDashboard: No valid adminUser in Redux, checking service');
        const user = adminAuthService.getCurrentAdmin();
        if (!user || !user.username || !user.role) {
          console.log('AdminDashboard: No valid user from service, redirecting to login');
          setLoading(true); // keep loading true if not authenticated
          // Save intended destination so login can redirect back after success
          const urlParams = new URLSearchParams(window.location.search);
          const intendedTab = urlParams.get('tab');
          const intendedData = urlParams.get('data');
          if (intendedTab && intendedTab !== 'dashboard') {
            sessionStorage.setItem('adminReturnTab', intendedTab);
            if (intendedData) sessionStorage.setItem('adminReturnData', intendedData);
          }
          // Use hard navigation to prevent React Router URL-sync effects from
          // overriding this redirect and creating an infinite loop.
          window.location.href = '/admin/login';
          return;
        }
        console.log('AdminDashboard: Valid user from service:', user.username);
      }
      console.log('AdminDashboard: Setting loading to FALSE');
      loadingSetToFalseRef.current = true;
      setLoading(false); // set loading false only if user is valid
      // Only load notifications once after confirming user is authenticated
      if (!notificationsLoadedRef.current && !notificationsLoadingRef.current) {
        console.log('AdminDashboard: Loading notifications for the first time');
        notificationsLoadingRef.current = true;
        try {
        await loadNotifications();
          console.log('AdminDashboard: Notifications loaded successfully');
        } catch (notifError) {
          console.error('AdminDashboard: Failed to load notifications:', notifError);
        }
        notificationsLoadedRef.current = true;
        notificationsLoadingRef.current = false;
      } else {
        console.log('AdminDashboard: Skipping notifications load', { 
          notificationsLoadedRef: notificationsLoadedRef.current,
          notificationsLoadingRef: notificationsLoadingRef.current 
        });
      }
      console.log('AdminDashboard: checkAdminAuth COMPLETE');
    } catch (error) {
      console.error('AdminDashboard: checkAdminAuth ERROR:', error);
      setLoading(true); // keep loading true on error
      window.location.href = '/admin/login';
    }
  }, [navigate, adminUser]);

  const refreshAdminUserData = useCallback(async () => {
    try {
      const response = await adminApi.getAdminProfile();
      if (response.success && response.data) {
        // Update Redux state instead of local state
        dispatch(setAdminUser(response.data));
      }
    } catch (error) {
      console.error('Failed to refresh admin user data:', error);
    }
  }, [dispatch]); // Removed adminApi dependency since it's now stable

  const handleLogout = useCallback(async () => {
    try {
      // Clear all localStorage data that could cause issues
      localStorage.removeItem('adminActiveTab');
      localStorage.removeItem('debugPanelPosition');
      // Clear any other admin-related localStorage items
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('admin') || key.startsWith('Admin')) {
          localStorage.removeItem(key);
        }
      });
      
      await adminAuthService.logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/admin/login');
    }
  }, [navigate]);

  const hasPermission = useCallback((permission: string): boolean => {
    // Default permissions for COMPLIANCE_OFFICER role
    const compliancePermissions = [
      'VIEW_COMPLIANCE_ALERTS',
      'VIEW_KYC',
      'VIEW_KYB',
      'MANAGE_COMPLIANCE_ALERTS',
      'VIEW_CUSTOMER_DETAILS',
      'VIEW_ANALYTICS',  // Added to allow dashboard access
      'VIEW_DASHBOARD_KYC_REVIEWS',  // Added for KYC dashboard card
      'VIEW_DASHBOARD_KYB_REVIEWS'   // Added for KYB dashboard card
    ];
    
    const hasPerm = 
      // Check explicit permissions
      adminUser?.permissions?.includes(permission) || 
      // Compliance officers get their default permissions
      (adminUser?.role === 'COMPLIANCE_OFFICER' && compliancePermissions.includes(permission)) ||
      // Super admins have access to SUPER_ADMIN-gated items
      (permission === 'SUPER_ADMIN' && adminUser?.role === 'SUPER_ADMIN');
    
    console.log('hasPermission check:', { 
      permission, 
      hasPerm, 
      userPermissions: adminUser?.permissions, 
      userRole: adminUser?.role,
      department: adminUser?.department,
      isComplianceOfficer: adminUser?.role === 'COMPLIANCE_OFFICER',
      hasDefaultPermission: compliancePermissions.includes(permission)
    });
    
    return hasPerm;
  }, [adminUser]);

  useEffect(() => {
    if (adminUser?.permissions?.length) {
      cachedAdminPermissionsRef.current = adminUser.permissions;
    }
  }, [adminUser?.permissions]);

  /** During JWT refresh, permissions briefly unavailable — use last known set. */
  const hasEffectivePermission = useCallback((permission: string): boolean => {
    if (adminUser?.role === 'SUPER_ADMIN') return true;
    if (authRefreshing && cachedAdminPermissionsRef.current.length > 0) {
      return cachedAdminPermissionsRef.current.includes(permission);
    }
    return hasPermission(permission);
  }, [adminUser?.role, authRefreshing, hasPermission]);

  /** Sidebar / mobile nav: Market signals tab is shown for signal viewers or anyone who can manage the landing page. */
  const navItemVisible = useCallback(
    (item: { id: string; permission: string }) => {
      if (item.id === 'marketSignals') {
        return (
          adminUser?.role === 'SUPER_ADMIN' ||
          !!adminUser?.permissions?.includes('VIEW_MARKET_SIGNALS') ||
          !!adminUser?.permissions?.includes('MANAGE_LANDING_PAGE') ||
          !!adminUser?.permissions?.includes('VIEW_LANDING_PAGE')
        );
      }
      if (item.id === 'subscriptionCryptoApprovals') {
        return (
          adminUser?.role === 'SUPER_ADMIN' ||
          !!adminUser?.permissions?.includes('APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS') ||
          !!adminUser?.permissions?.includes('MANAGE_SUBSCRIPTIONS')
        );
      }
      if (item.id === 'adminSubscriptionCryptoApprovals') {
        return (
          adminUser?.role === 'SUPER_ADMIN' ||
          !!adminUser?.permissions?.includes('APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS') ||
          !!adminUser?.permissions?.includes('MANAGE_SUBSCRIPTIONS')
        );
      }
      return hasPermission(item.permission);
    },
    [adminUser, hasPermission]
  );

  const getTabIcon = useCallback((tab: string): React.ReactNode => {
    const iconStyle = { color: '#e7038e', fontSize: '18px' };
    const mobileIconStyle = { color: '#e7038e', fontSize: '22px' };
    
    const icons: { [key: string]: React.ReactNode } = {
      dashboard: <FaChartBar style={iconStyle} />,
      customers: <FaUsers style={iconStyle} />,
      accounts: <FaCreditCard style={iconStyle} />,
      transactions: <FaMoneyBillWave style={iconStyle} />,
      compliance: <FaExclamationTriangle style={iconStyle} />,
      reports: <FaChartLine style={iconStyle} />,
      adminUsers: <FaUserTie style={iconStyle} />,
      auditLogs: <FaClipboardList style={iconStyle} />,
      blocklist: <FaBan style={iconStyle} />,
      notifications: <FaBell style={iconStyle} />,
      profile: <FaUser style={iconStyle} />,
      reviewQueue: <FaEdit style={iconStyle} />,
      providerConfig: <FaCog style={iconStyle} />,
      socialMedia: <FaMobileAlt style={iconStyle} />,
      assetTokens: <FaFileAlt style={iconStyle} />,
      multiSignatureWallets: <FaLock style={iconStyle} />,
      kyt: <FaSearch style={iconStyle} />,
      settings: <FaCog style={iconStyle} />,
      // New menu icons
      tradingBots: <FaRobot style={iconStyle} />,
      cryptoPortfolio: <FaCoins style={iconStyle} />,
      stockPortfolio: <FaChartLine style={iconStyle} />,
      riskManagement: <FaShieldAlt style={iconStyle} />,
      systemHealth: <FaHeart style={iconStyle} />,
      eligibilityManagement: <FaCheckCircle style={iconStyle} />,
      adminSettings: <FaCog style={iconStyle} />,
      myTradingAccounts: <FaCoins style={iconStyle} />,
      products: <FaCreditCard style={iconStyle} />,
      igaming: <FaDice style={iconStyle} />,
      p2pGames: <FaGamepad style={iconStyle} />,
      cryptoPayouts: <FaCoins style={iconStyle} />,
      platformCoins: <FaCoins style={iconStyle} />,
      tradingProvider: <FaCog style={iconStyle} />,
      orderBook: <FaBook style={iconStyle} />,
      matchingEngine: <FaChartLine style={iconStyle} />,
      stablecoinManagement: <FaExchangeAlt style={iconStyle} />,
      clientOnboarding: <FaRocket style={iconStyle} />,
      clientFeatureSelector: <FaCheckCircle style={iconStyle} />,
      truelayerTopUp: <FaCoins style={iconStyle} />,
      landingPage: <FaFileAlt style={iconStyle} />,
      marketSignals: <FaChartLine style={iconStyle} />,
      markets: <FaExchangeAlt style={iconStyle} />,
      faq: <span style={{ fontSize: 16, lineHeight: 1 }}>❓</span>,
      creatorChat: <FaComments style={iconStyle} />,
      loginDisplaySettings: <FaCog style={iconStyle} />,
      superAdminSubscriptions: <FaFileAlt style={iconStyle} />,
      subscriptionCryptoApprovals: <FaClipboardCheck style={iconStyle} />,
      adminSubscriptionCryptoApprovals: <FaClipboardCheck style={iconStyle} />,
    };
    return icons[tab] || <FaFileAlt style={iconStyle} />;
  }, []);

  const navigationItems = useMemo(() => {
    const items = [
      // ✅ DIRECT MAPPINGS - These have exact Admin Sidebar Menu Access permissions
      { id: 'dashboard', label: 'Dashboard', permission: 'VIEW_ADMIN_DASHBOARD' },
      { id: 'customers', label: 'Customers', permission: 'VIEW_ADMIN_CUSTOMERS' },
      { id: 'compliance', label: 'Compliance', permission: 'VIEW_ADMIN_COMPLIANCE' },
      { id: 'kyt', label: 'KYT Analysis', permission: 'VIEW_ADMIN_KYT' },
      { id: 'accounts', label: 'Accounts', permission: 'VIEW_ADMIN_ACCOUNTS' },
      { id: 'transactions', label: 'Transactions', permission: 'VIEW_ADMIN_TRANSACTIONS' },
      { id: 'reports', label: 'Reports', permission: 'VIEW_ADMIN_REPORTS' },
      { id: 'adminUsers', label: 'Admin Users', permission: 'VIEW_ADMIN_USERS' },
      { id: 'auditLogs', label: 'Audit Logs', permission: 'VIEW_ADMIN_AUDIT' },
      { id: 'blocklist', label: 'Blocklist', permission: 'VIEW_IP_BLOCKLIST' },
      { id: 'adminSettings', label: 'Admin Settings', permission: 'VIEW_ADMIN_SETTINGS' },
      { id: 'products', label: 'Products', permission: 'VIEW_ADMIN_PRODUCTS' },
      { id: 'igaming', label: 'iGaming', permission: 'VIEW_ADMIN_IGAMING' },
      
      // ✅ NEW DEDICATED PERMISSIONS - Separate permissions for Trading Bots, Social Media, and P2P Games
      { id: 'tradingBots', label: 'Trading Bots', permission: 'VIEW_ADMIN_TRADING_BOTS' },
      { id: 'cryptoPortfolio', label: 'Crypto Portfolio', permission: 'VIEW_CRYPTO_PORTFOLIO' },
      { id: 'stockPortfolio', label: 'Stock Portfolio', permission: 'VIEW_STOCK_PORTFOLIO' },
      { id: 'tradingProvider', label: 'Trading Provider', permission: 'MANAGE_TRADING_BOTS' },
      { id: 'myTradingAccounts', label: 'My Trading Accounts', permission: 'MANAGE_TRADING_BOTS' },
      { id: 'kucoinSubaccountProvisioning', label: 'KuCoin Sub-Accounts', permission: 'PROVISION_KUCOIN_SUBACCOUNTS' },
      { id: 'orderBook', label: 'Order Book', permission: 'VIEW_ADMIN_ORDER_BOOK' },
      { id: 'markets', label: 'Market Intelligence', permission: 'VIEW_ADMIN_MARKETS' },
      { id: 'matchingEngine', label: 'Matching Engine', permission: 'MANAGE_MATCHING_ENGINE' },
      { id: 'socialMedia', label: 'Social Media', permission: 'VIEW_ADMIN_SOCIAL_MEDIA' },
      { id: 'p2pGames', label: 'P2P Games', permission: 'VIEW_ADMIN_P2PGAME' },
      { id: 'cryptoPayouts', label: 'Crypto Payouts', permission: 'VIEW_ADMIN_CRYPTOPAYOUTS' },
      { id: 'platformCoins', label: 'Platform Coins', permission: 'VIEW_PLATFORM_COINS' },
      { id: 'truelayerTopUp', label: 'TrueLayer Top-Ups', permission: 'VIEW_TRUELAYER_TOPUP' },
      
      // ✅ MAPPED TO EXISTING PERMISSIONS - Using permissions that exist in your system
      { id: 'riskManagement', label: 'Risk Management', permission: 'VIEW_ADMIN_ANALYTICS' },
      { id: 'systemHealth', label: 'System Health', permission: 'VIEW_ADMIN_ANALYTICS' },
      { id: 'eligibilityManagement', label: 'Eligibility Management', permission: 'VIEW_ADMIN_ANALYTICS' },
      { id: 'assetTokens', label: 'Asset Tokens', permission: 'VIEW_ADMIN_ASSET_TOKENS' },
      { id: 'stablecoinManagement', label: 'Stablecoin Management', permission: 'VIEW_ADMIN_STABLECOIN' },
      { id: 'superAdminSubscriptions', label: 'Subscriptions', permission: 'VIEW_ADMIN_SUBSCRIPTIONS' },
      { id: 'subscriptionCryptoApprovals', label: 'Retail Subscription Requests', permission: 'APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS' },
      { id: 'adminSubscriptionCryptoApprovals', label: 'Admin Subscription Requests', permission: 'APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS' },
      { id: 'landingPage', label: 'Landing Page', permission: 'VIEW_LANDING_PAGE' },
      { id: 'marketSignals', label: 'Market signals', permission: 'VIEW_MARKET_SIGNALS' },
      { id: 'faq', label: 'FAQ', permission: 'VIEW_FAQ' },
      { id: 'creatorChat', label: 'Creator Chat', permission: 'VIEW_CREATOR_CHAT' },
      { id: 'loginDisplaySettings', label: 'Login Display', permission: 'MANAGE_LOGIN_DISPLAY_SETTINGS' },
      { id: 'clientOnboarding', label: 'Client Onboarding', permission: 'MANAGE_ADMIN_USERS' },
      { id: 'clientFeatureSelector', label: 'Feature Selector', permission: 'VIEW_CLIENT_FEATURE_SELECTOR' },
      { id: 'multiSignatureWallets', label: 'Multi-Signature Wallets', permission: 'VIEW_ADMIN_INTEGRATIONS' },
      { id: 'reviewQueue', label: 'Review Queue', permission: 'VIEW_ADMIN_ANALYTICS' },
      { id: 'providerConfig', label: 'Provider Config', permission: 'VIEW_ADMIN_INTEGRATIONS' },
    ];

    // Always keep Dashboard first, then sort the rest
    const dashboardItem = items.find(item => item.id === 'dashboard');
    const otherItems = items.filter(item => item.id !== 'dashboard');

    // For compliance officers, prioritize compliance-related items in the remaining items
    if (adminUser?.role === 'COMPLIANCE_OFFICER') {
      const sortedOtherItems = otherItems.sort((a, b) => {
        const complianceItems = ['compliance', 'kyt'];
        const aIsCompliance = complianceItems.includes(a.id);
        const bIsCompliance = complianceItems.includes(b.id);
        if (aIsCompliance && !bIsCompliance) return -1;
        if (!aIsCompliance && bIsCompliance) return 1;
        return 0;
      });
      
      return dashboardItem ? [dashboardItem, ...sortedOtherItems] : sortedOtherItems;
    }

    return dashboardItem ? [dashboardItem, ...otherItems] : otherItems;
  }, [adminUser?.role]);

  const handleTabChange = useCallback((tabId: string) => {
    console.log('handleTabChange called with tabId:', tabId, 'current activeTab:', activeTab);
    
    // Prevent URL sync effect from interfering with this user action
    isUpdatingFromUrlRef.current = true;
    
    setActiveTab(tabId);
    localStorage.setItem('adminActiveTab', tabId);
    
    // URL will be updated by the useEffect that syncs activeTab with URL
    if (isMobile) {
      setMobileMenuOpen(false);
    }
    
    // Reset flag after state updates complete
    setTimeout(() => {
      isUpdatingFromUrlRef.current = false;
    }, 150);
  }, [isMobile, activeTab]);

  const handleNotificationClick = useCallback(() => {
    // Prevent URL sync effect from interfering with this user action
    isUpdatingFromUrlRef.current = true;
    
    setActiveTab('notifications');
    if (isMobile) {
      setMobileMenuOpen(false);
    }
    
    // Reset flag after state updates complete
    setTimeout(() => {
      isUpdatingFromUrlRef.current = false;
    }, 150);
  }, [isMobile]);

  // Memoize DashboardStats to prevent unnecessary re-creation
  const MemoizedDashboardStats = useMemo(() => {
    console.log('AdminDashboard: Creating memoized DashboardStats component');
    return <DashboardStats key="dashboard-stats" />;
  }, []); // Empty dependency array - this component should never change

  const renderContent = useMemo(() => {
    console.log('AdminDashboard: renderContent called with activeTab:', activeTab, 'adminUser:', adminUser?.username);

    // ── Permission guard ──────────────────────────────────────────────────────
    // Build a tab → required-permission lookup from navigationItems so that
    // directly typing ?tab=xxx in the URL is also blocked when the user lacks
    // the permission (not just hidden from the sidebar).
    const canViewMarketSignalsTab =
      adminUser?.role === 'SUPER_ADMIN' ||
      hasEffectivePermission('VIEW_MARKET_SIGNALS') ||
      hasEffectivePermission('MANAGE_LANDING_PAGE') ||
      hasEffectivePermission('VIEW_LANDING_PAGE');

    const canViewSubscriptionCryptoApprovals =
      adminUser?.role === 'SUPER_ADMIN' ||
      hasPermission('APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS') ||
      hasPermission('MANAGE_SUBSCRIPTIONS');
    const canViewAdminSubscriptionCryptoApprovals =
      adminUser?.role === 'SUPER_ADMIN' ||
      hasPermission('APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS') ||
      hasPermission('MANAGE_SUBSCRIPTIONS');

    if (activeTab === 'subscriptionCryptoApprovals' && !canViewSubscriptionCryptoApprovals) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '60vh', gap: 16,
          color: '#6b7280', fontFamily: 'Nunito, sans-serif'
        }}>
          <svg width="56" height="56" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="2"/>
            <path d="M12 8v4m0 4h.01" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: 0 }}>
            Access Denied
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0, textAlign: 'center', maxWidth: 400 }}>
            You need <strong>APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS</strong> or <strong>MANAGE_SUBSCRIPTIONS</strong> (or Super Admin role) to open this view.
          </p>
        </div>
      );
    }
    if (activeTab === 'adminSubscriptionCryptoApprovals' && !canViewAdminSubscriptionCryptoApprovals) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '60vh', gap: 16,
          color: '#6b7280', fontFamily: 'Nunito, sans-serif'
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: 0 }}>
            Access Denied
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0, textAlign: 'center', maxWidth: 400 }}>
            You need <strong>APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS</strong> or <strong>MANAGE_SUBSCRIPTIONS</strong> (or Super Admin role) to open this view.
          </p>
        </div>
      );
    }

    if (activeTab === 'marketSignals' && !canViewMarketSignalsTab) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '60vh', gap: 16,
          color: '#6b7280', fontFamily: 'Nunito, sans-serif'
        }}>
          <svg width="56" height="56" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="2"/>
            <path d="M12 8v4m0 4h.01" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: 0 }}>
            Access Denied
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0, textAlign: 'center', maxWidth: 360 }}>
            You need <strong>VIEW_MARKET_SIGNALS</strong>, or landing page access (<strong>VIEW_LANDING_PAGE</strong> / <strong>MANAGE_LANDING_PAGE</strong>), to open this view.
          </p>
        </div>
      );
    }

    const tabPermissionMap: Record<string, string> = {
      dashboard: 'VIEW_ADMIN_DASHBOARD',
      customers: 'VIEW_ADMIN_CUSTOMERS',
      compliance: 'VIEW_ADMIN_COMPLIANCE',
      kyt: 'VIEW_ADMIN_KYT',
      accounts: 'VIEW_ADMIN_ACCOUNTS',
      transactions: 'VIEW_ADMIN_TRANSACTIONS',
      reports: 'VIEW_ADMIN_REPORTS',
      adminUsers: 'VIEW_ADMIN_USERS',
      auditLogs: 'VIEW_ADMIN_AUDIT',
      blocklist: 'VIEW_IP_BLOCKLIST',
      adminSettings: 'VIEW_ADMIN_SETTINGS',
      products: 'VIEW_ADMIN_PRODUCTS',
      igaming: 'VIEW_ADMIN_IGAMING',
      tradingBots: 'VIEW_ADMIN_TRADING_BOTS',
      cryptoPortfolio: 'VIEW_CRYPTO_PORTFOLIO',
      stockPortfolio: 'VIEW_STOCK_PORTFOLIO',
      tradingProvider: 'MANAGE_TRADING_BOTS',
      myTradingAccounts: 'MANAGE_TRADING_BOTS',
      kucoinSubaccountProvisioning: 'PROVISION_KUCOIN_SUBACCOUNTS',
      orderBook: 'VIEW_ADMIN_ORDER_BOOK',
      markets: 'VIEW_ADMIN_MARKETS',
      matchingEngine: 'MANAGE_MATCHING_ENGINE',
      socialMedia: 'VIEW_ADMIN_SOCIAL_MEDIA',
      p2pGames: 'VIEW_ADMIN_P2PGAME',
      cryptoPayouts: 'VIEW_ADMIN_CRYPTOPAYOUTS',
      platformCoins: 'VIEW_PLATFORM_COINS',
      riskManagement: 'VIEW_ADMIN_ANALYTICS',
      systemHealth: 'VIEW_ADMIN_ANALYTICS',
      eligibilityManagement: 'VIEW_ADMIN_ANALYTICS',
      assetTokens: 'VIEW_ADMIN_ASSET_TOKENS',
      stablecoinManagement: 'VIEW_ADMIN_STABLECOIN',
      clientOnboarding: 'MANAGE_ADMIN_USERS',
      clientFeatureSelector: 'VIEW_CLIENT_FEATURE_SELECTOR',
      multiSignatureWallets: 'VIEW_ADMIN_INTEGRATIONS',
      reviewQueue: 'VIEW_ADMIN_ANALYTICS',
      providerConfig: 'VIEW_ADMIN_INTEGRATIONS',
      truelayerTopUp: 'VIEW_TRUELAYER_TOPUP',
      kytAnalysis: 'VIEW_ADMIN_KYT',
      igamingManagement: 'VIEW_ADMIN_IGAMING',
      loginDisplaySettings: 'MANAGE_LOGIN_DISPLAY_SETTINGS',
      creatorChat: 'VIEW_CREATOR_CHAT',
    };

    const requiredPermission = tabPermissionMap[activeTab];
    if (requiredPermission && !hasEffectivePermission(requiredPermission)) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '60vh', gap: 16,
          color: '#6b7280', fontFamily: 'Nunito, sans-serif'
        }}>
          <svg width="56" height="56" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="2"/>
            <path d="M12 8v4m0 4h.01" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: 0 }}>
            Access Denied
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0, textAlign: 'center', maxWidth: 340 }}>
            You don't have permission to view this page.<br />
            Required permission: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{requiredPermission}</code>
          </p>
          <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
            Contact your Super Admin to request access.
          </p>
        </div>
      );
    }
    // ─────────────────────────────────────────────────────────────────────────

    switch (activeTab) {
      case 'dashboard':
        console.log('AdminDashboard: Rendering DashboardStats component');
        return MemoizedDashboardStats;
      case 'customers':
        console.log('AdminDashboard: Rendering CustomerManagement component');
        return <CustomerManagement />;
      case 'accounts':
        console.log('AdminDashboard: Rendering AccountManagement component');
        return <AccountManagement />;
      case 'transactions':
        console.log('AdminDashboard: Rendering TransactionManagement component');
        return <TransactionManagement />;
      case 'compliance':
        console.log('AdminDashboard: Rendering ComplianceAlerts component');
        return <ComplianceAlerts />;
      case 'reports':
        console.log('AdminDashboard: Rendering ReportsAnalytics component');
        return <ReportsAnalytics />;
      case 'adminUsers':
        console.log('AdminDashboard: Rendering AdminUserManagement component');
        return <AdminUserManagement />;
      case 'reviewQueue':
        return <ReviewQueue />;
      case 'auditLogs':
        console.log('AdminDashboard: Rendering AuditLogs component');
        return <AuditLogs />;
      case 'blocklist':
        console.log('AdminDashboard: Rendering IpBlocklist component');
        return <IpBlocklist />;
      case 'notifications':
        console.log('AdminDashboard: Rendering AdminNotifications component');
        return <AdminNotifications />;
      case 'profile':
        console.log('AdminDashboard: Rendering AdminProfile component');
        return <AdminProfile adminUser={adminUser} onProfileUpdate={refreshAdminUserData} setActiveTab={setActiveTab} />;
      case 'providerConfig':
        console.log('AdminDashboard: Rendering ProviderConfigManagement component');
        return <ProviderConfigManagement />;
      case 'tradingProvider':
        console.log('AdminDashboard: Rendering TradingProviderSelection component');
        return <TradingProviderSelection />;
      case 'myTradingAccounts':
        console.log('AdminDashboard: Rendering AdminTradingAccounts component');
        return <AdminTradingAccounts />;
      case 'kucoinSubaccountProvisioning':
        return <KucoinSubaccountProvisionerPanel />;
      case 'tradingBots':
        return <TradingBotsPanel />;
      case 'cryptoPortfolio':
        return <CryptoPortfolio isAdminContext />;
      case 'stockPortfolio':
        return <StockPortfolio />;
      case 'orderBook':
        return <OrderBookManagement />;
      case 'matchingEngine':
        return <MatchingEngineManagement />;
      case 'riskManagement':
        return <RiskManagementPanel />;
      case 'systemHealth':
        return <SystemHealthPanel />;
      case 'eligibilityManagement':
        return <EligibilityManagementPanel />;
      case 'assetTokens':
        console.log('AdminDashboard: Rendering AssetTokens component');
        return <AssetTokens />;
      case 'stablecoinManagement':
        console.log('AdminDashboard: Rendering AdminStablecoinManagement component');
        return <AdminStablecoinManagement />;
      case 'clientOnboarding':
        console.log('AdminDashboard: Rendering ClientOnboarding component');
        return <ClientOnboarding />;
      case 'clientFeatureSelector':
        console.log('AdminDashboard: Rendering ClientFeatureSelector component');
        return <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}><ClientFeatureSelector /></div>;
      case 'multiSignatureWallets':
        console.log('AdminDashboard: Rendering MultiSignatureWallets component');
        return <MultiSignatureWallets />;
      case 'socialMedia':
        console.log('AdminDashboard: Rendering SocialMediaContentManagement component');
        return <SocialMediaContentManagement />;
      case 'adminSettings':
        console.log('AdminDashboard: Rendering AdminSettings component');
        return <AdminSettings />;
      case 'kyt':
        console.log('AdminDashboard: Rendering KYT component');
        return <KYTDashboard />;
      case 'products':
        console.log('AdminDashboard: Rendering ProductManagement component');
        return <ProductManagement />;
      case 'igaming':
        console.log('AdminDashboard: Rendering IGamingManagement component');
        return <IGamingManagement />;
      case 'p2pGames':
        console.log('AdminDashboard: Rendering P2PGamesManagement component');
        return <P2PGamesManagement />;
      case 'cryptoPayouts':
        console.log('AdminDashboard: Rendering AdminCryptoPayouts component');
        return <AdminCryptoPayouts />;
      case 'platformCoins':
        return <AdminPlatformCoins />;
      case 'truelayerTopUp':
        return <AdminTrueLayerTopUp />;
      case 'superAdminSubscriptions':
        return <SuperAdminSubscriptions />;
      case 'subscriptionCryptoApprovals':
        return <SubscriptionCryptoPaymentApprovals />;
      case 'adminSubscriptionCryptoApprovals':
        return <AdminSubscriptionCryptoPaymentApprovals />;
      case 'landingPage':
        return <AdminLandingPageManager />;
      case 'faq':
        return <AdminFaqManager />;
      case 'creatorChat':
        return <AdminCreatorChatModalBuilder />;
      case 'loginDisplaySettings':
        return <AdminLoginDisplaySettings />;
      case 'marketSignals':
        return (
          <div style={{ margin: 0, minHeight: 0, width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
            <MarketLanding isAdminMarketSignalsEmbed />
          </div>
        );
      case 'markets':
        return <AdminMarkets />;
      default:
        console.log('AdminDashboard: Rendering default DashboardStats component');
        return MemoizedDashboardStats;
    }
  }, [activeTab, MemoizedDashboardStats, adminUser, hasPermission, hasEffectivePermission]);

  if (loading || !adminUser || !isAuthenticated) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading Admin Dashboard...</p>
      </div>
    );
  }

  return (
    <>
      {/* Admin Subscription Expired Banner */}
      {adminSubExpiredMessage && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: '#f8d7da',
            borderBottom: '2px solid #f5c6cb',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <strong style={{ color: '#721c24', fontSize: '14px' }}>Admin Subscription Expired:&nbsp;</strong>
              <span style={{ color: '#491217', fontSize: '14px' }}>{adminSubExpiredMessage}</span>
              {adminRenewEligible && (
                <button
                  type="button"
                  onClick={() => setRenewModalOpen(true)}
                  disabled={renewBusy}
                  style={{
                    marginLeft: '8px',
                    padding: '6px 14px',
                    background: '#721c24',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: renewBusy ? 'wait' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {renewBusy ? 'Starting…' : 'Renew subscription'}
                </button>
              )}
            </div>
            {renewError && (
              <div style={{ color: '#721c24', fontSize: '13px', paddingLeft: '28px' }}>{renewError}</div>
            )}
          </div>
          <button
            onClick={() => setAdminSubExpiredMessage(null)}
            aria-label="Dismiss admin subscription warning"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#721c24',
              fontSize: '18px',
              padding: '4px',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>
      )}
      <div className="admin-dashboard">
        {adminRenewEligible && !adminSubExpiredMessage && (
          <div
            role="status"
            style={{
              margin: '0 0 12px',
              padding: '12px 16px',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '12px',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ color: '#856404', fontSize: '14px' }}>
              Your admin subscription is not active. Renew to restore full portal access.
            </span>
            <button
              type="button"
              onClick={() => setRenewModalOpen(true)}
              disabled={renewBusy}
              style={{
                padding: '8px 16px',
                background: '#856404',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: renewBusy ? 'wait' : 'pointer',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {renewBusy ? 'Starting…' : 'Renew subscription'}
            </button>
            {renewError && (
              <span style={{ width: '100%', color: '#721c24', fontSize: '13px' }}>{renewError}</span>
            )}
          </div>
        )}
        {pendingAdminRenewal?.hasPending && (
          <div
            role="status"
            style={{
              margin: '0 0 12px',
              padding: '12px 16px',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ color: '#1e3a5f', fontSize: 14, fontWeight: 700 }}>
              Admin subscription renewal in progress
            </div>
            <div style={{ color: '#334155', fontSize: 13 }}>
              {pendingAdminRenewal.message || 'We are processing your renewal.'}
            </div>
            {pendingAdminRenewal.status === 'AWAITING_PAYMENT' &&
              String(pendingAdminRenewal.paymentMethod || '').toUpperCase() === 'CRYPTO' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <div style={{ background: '#fff', border: '1px solid #dbeafe', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                      <strong>Chain:</strong> {String(pendingAdminRenewal.blockchain || pendingAdminRenewal.cryptoBlockchain || '-').toUpperCase()}
                    </div>
                    <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                      <strong>Symbol:</strong> {pendingAdminRenewal.symbol || pendingAdminRenewal.cryptoSymbol || '-'}
                    </div>
                    <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
                      <strong>Amount:</strong> {pendingAdminRenewal.amount || '-'} {pendingAdminRenewal.currency || ''}
                    </div>
                    <div style={{ fontSize: 12, color: '#334155', wordBreak: 'break-all' }}>
                      <strong>Destination:</strong> {pendingAdminRenewal.destinationAddress || pendingAdminRenewal.cryptoDestinationAddress || '-'}
                    </div>
                    {renewDestinationQr && (
                      <div style={{ marginTop: 8 }}>
                        <img src={renewDestinationQr} alt="Renewal destination wallet QR" style={{ width: 120, height: 120 }} />
                      </div>
                    )}
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #dbeafe', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
                      <strong>Your Zogzila wallet (same chain):</strong>
                    </div>
                    <div style={{ fontSize: 12, color: '#334155', marginBottom: 8, wordBreak: 'break-all' }}>
                      {renewOwnWalletAddress || 'No wallet found on this network.'}
                    </div>
                    {renewOwnWalletQr && (
                      <div style={{ marginBottom: 8 }}>
                        <img src={renewOwnWalletQr} alt="Your wallet QR" style={{ width: 120, height: 120 }} />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => void payAdminRenewalWithWallet()}
                      disabled={renewWalletBusy}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#086afb',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: renewWalletBusy ? 'wait' : 'pointer',
                      }}
                    >
                      {renewWalletBusy ? 'Submitting…' : 'Pay with my Zogzila wallet'}
                    </button>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #dbeafe', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
                      <strong>Paid externally?</strong> Submit tx hash:
                    </div>
                    <input
                      value={renewTxHash}
                      onChange={(e) => setRenewTxHash(e.target.value)}
                      placeholder="0x... / tx hash"
                      style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                    />
                    <button
                      type="button"
                      onClick={() => void submitAdminRenewalCryptoProof()}
                      disabled={renewTxBusy || !renewTxHash.trim()}
                      style={{
                        marginTop: 8,
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: 999,
                        border: '1px solid #86aee6',
                        background: '#fff',
                        color: '#1e3a5f',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: renewTxBusy ? 'wait' : 'pointer',
                      }}
                    >
                      {renewTxBusy ? 'Submitting…' : 'Submit tx hash'}
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}
        {/* Mobile Header */}
        {isMobile && (
          <header className="mobile-header">
            <div className="mobile-header-content">
              <div className="mobile-logo">
                <div className="mobile-logo-icon">
                  <AdminLogo size={28} />
                </div>
                <div className="mobile-logo-text">Banking Admin</div>
              </div>
              <div className="mobile-user-info">
                <div className="mobile-notifications">
                  <button className="mobile-notification-btn" onClick={handleNotificationClick}>
                    🔔
                    {notifications.length > 0 && (
                      <span className="mobile-notification-badge">{notifications.length}</span>
                    )}
                  </button>
                </div>
                <div className="mobile-profile">
                  <div className="mobile-avatar">
                    {adminUser?.fullName?.charAt(0) || 'A'}
                  </div>
                  <div className="mobile-user-name">{adminUser?.fullName?.split(' ')[0] || 'Admin'}</div>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Sidebar */}
        <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Mobile draggable handle - similar to Retail version */}
          {isMobile && mobileMenuOpen && (
            <div 
              className="sidebar-handle"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                width: '100%',
                padding: '12px 0 8px 0',
                textAlign: 'center',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              <div style={{
                width: '40px',
                height: '4px',
                background: '#e0e0e0',
                borderRadius: '2px',
                margin: '0 auto'
              }}></div>
            </div>
          )}
          
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <div className="logo-icon">
                <AdminLogo size={32} />
              </div>
              {!sidebarCollapsed && (
                <div className="logo-text">
                  <h3>Zogzila</h3>
                  <p>Admin Portal</p>
                </div>
              )}
            </div>
            {!isMobile && (
              <button
                className="sidebar-toggle"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                {sidebarCollapsed ? '→' : '←'}
              </button>
            )}
          </div>
          
          <nav className="sidebar-nav" ref={sidebarNavRef}>
            {(() => {
              const filteredItems = navigationItems.filter(item => navItemVisible(item));
              console.log('Navigation items:', { 
                all: navigationItems.map(item => ({ id: item.id, permission: item.permission })),
                filtered: filteredItems.map(item => ({ id: item.id, permission: item.permission })),
                activeTab
              });
              return filteredItems.map(item => {
                if (item.id === 'adminSettings') {
                  return (
                    <button
                      key={item.id}
                      className={`nav-item ${activeTab === 'adminSettings' ? 'active' : ''}`}
                      onClick={() => handleTabChange('adminSettings')}
                    >
                      <span className="nav-icon">{getTabIcon('settings')}</span>
                      {!sidebarCollapsed && (
                        <div className="nav-content">
                          <span className="nav-label">{item.label}</span>
                        </div>
                      )}
                    </button>
                  );
                }
                if (item.id === 'tradingBotsLink') {
                  return (
                    <button
                      key={item.id}
                      className={`nav-item`}
                      onClick={() => navigate('/admin/trading-bots')}
                    >
                      <span className="nav-icon">{getTabIcon('tradingBots')}</span>
                      {!sidebarCollapsed && (
                        <div className="nav-content">
                          <span className="nav-label">{item.label}</span>
                        </div>
                      )}
                    </button>
                  );
                }
                if (item.id === 'kyt') {
                    return (
                      <button
                        key={item.id}
                        className={`nav-item ${activeTab === 'kyt' ? 'active' : ''}`}
                        onClick={() => setActiveTab('kyt')}
                      >
                        <span className="nav-icon">{getTabIcon('kyt')}</span>
                        {!sidebarCollapsed && (
                          <div className="nav-content">
                            <span className="nav-label">{item.label}</span>
                          </div>
                        )}
                      </button>
                    );
                }
                return (
                  <button
                    key={item.id}
                    className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => handleTabChange(item.id)}
                  >
                    <span className="nav-icon">{getTabIcon(item.id)}</span>
                    {!sidebarCollapsed && (
                      <div className="nav-content">
                        <span className="nav-label">{item.label}</span>
                      </div>
                    )}
                  </button>
                );
              });
            })()}
          </nav>
          
          <div className="sidebar-footer">
            <div className="admin-profile" onClick={() => handleTabChange('profile')} style={{ cursor: 'pointer' }}>
              <div className="profile-avatar">
                {adminUser?.avatarBase64 ? (
                  <img 
                    src={`data:image/jpeg;base64,${adminUser.avatarBase64}`}
                    alt="Admin Profile"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  adminUser?.fullName?.charAt(0) || 'A'
                )}
              </div>
              {!sidebarCollapsed && (
                <div className="profile-info">
                  <div className="profile-name">{adminUser?.fullName || 'Admin User'}</div>
                  <div className="profile-role">{adminUser?.role || 'ADMIN'}</div>
                </div>
              )}
            </div>
            {/* Subscription status badge in sidebar footer */}
            {!sidebarCollapsed && adminSubscriptionStatus && (
              <div style={{
                margin: '10px 0 8px 0',
                padding: '10px 12px',
                borderRadius: 10,
                background:
                  adminSubscriptionStatus.status === 'ACTIVE' ? '#d1fae5' :
                  adminSubscriptionStatus.status === 'PLATFORM_OWNER' ? '#dbeafe' :
                  adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '#fef9c3' :
                  adminSubscriptionStatus.status === 'SUSPENDED' ? '#ffedd5' :
                  adminSubscriptionStatus.status === 'EXPIRED' ? '#fee2e2' : '#f3f4f6',
                border: '1px solid',
                borderColor:
                  adminSubscriptionStatus.status === 'ACTIVE' ? '#6ee7b7' :
                  adminSubscriptionStatus.status === 'PLATFORM_OWNER' ? '#93c5fd' :
                  adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '#fde68a' :
                  adminSubscriptionStatus.status === 'SUSPENDED' ? '#fdba74' :
                  adminSubscriptionStatus.status === 'EXPIRED' ? '#fca5a5' : '#e5e7eb',
                fontSize: 12,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 3, color: '#1e293b' }}>
                  🏷️ {adminSubscriptionStatus.planName || 'No Plan'}
                </div>
                <div style={{
                  color:
                    adminSubscriptionStatus.status === 'ACTIVE' ? '#065f46' :
                    adminSubscriptionStatus.status === 'PLATFORM_OWNER' ? '#1d4ed8' :
                    adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '#92400e' :
                    adminSubscriptionStatus.status === 'SUSPENDED' ? '#9a3412' :
                    adminSubscriptionStatus.status === 'EXPIRED' ? '#991b1b' : '#6b7280',
                  fontWeight: 600,
                }}>
                  {adminSubscriptionStatus.status === 'PLATFORM_OWNER' ? '✓ Platform Owner' :
                   adminSubscriptionStatus.status === 'ACTIVE' ? '✓ Active' :
                   adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '⚠ Grace Period' :
                   adminSubscriptionStatus.status === 'SUSPENDED' ? '⛔ Suspended' :
                   adminSubscriptionStatus.status === 'EXPIRED' ? '✕ Expired' : 'No Subscription'}
                </div>
                {adminSubscriptionStatus.expiresAt && adminSubscriptionStatus.status !== 'PLATFORM_OWNER' && (
                  <div style={{ color: '#6b7280', marginTop: 2 }}>
                    Exp: {new Date(adminSubscriptionStatus.expiresAt).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric'
                    })}
                  </div>
                )}
                {adminSubscriptionStatus.maxStrategies && adminSubscriptionStatus.status === 'ACTIVE' && (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {myStrategyCount !== null && (
                      <span style={{ color: '#374151', fontSize: 11 }}>
                        Strategies:{' '}
                        <strong style={{ color: myStrategyCount >= adminSubscriptionStatus.maxStrategies ? '#dc2626' : '#065f46' }}>
                          {myStrategyCount}/{adminSubscriptionStatus.maxStrategies}
                        </strong>
                      </span>
                    )}
                    {enrolledUserCount !== null && adminSubscriptionStatus.maxRetailUsers && (
                      <span style={{ color: '#374151', fontSize: 11 }}>
                        Retail Users:{' '}
                        <strong style={{ color: enrolledUserCount >= adminSubscriptionStatus.maxRetailUsers ? '#dc2626' : '#065f46' }}>
                          {enrolledUserCount}/{adminSubscriptionStatus.maxRetailUsers}
                        </strong>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            <button className="logout-btn" onClick={handleLogout}>
              <span className="logout-icon"><FaSignOutAlt style={{ color: '#dc2626', fontSize: '16px' }} /></span>
              {!sidebarCollapsed && 'Logout'}
            </button>
          </div>
        </aside>

        <div className="admin-main">
          {/* Desktop Header */}
          {!isMobile && (
            <header className="admin-header">
              <div className="admin-header-left">
                <h1 className="admin-title">Zogzila Admin Portal</h1>
              </div>
              
              <div className="admin-header-right">
                {hasPermission && hasPermission('VIEW_ADMIN_NOTIFICATIONS') && (
                  <div className="admin-notifications">
                    <button className="notification-btn" onClick={handleNotificationClick}>
                      🔔
                      {notifications.length > 0 && (
                        <span className="notification-badge">{notifications.length}</span>
                      )}
                    </button>
                  </div>
                )}
                
                <div className="admin-user-info">
                  <div className="admin-user-details">
                    <span className="admin-user-name">{adminUser?.fullName}</span>
                    <span className="admin-user-role">{adminUser?.role}</span>
                  </div>
                  <button className="admin-logout-btn" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              </div>
            </header>
          )}

          {/* Content */}
          <main className="admin-content">
            <div className="admin-content-header">
              <h2 className="content-title">
                {getTabIcon(activeTab)} {navigationItems.find(item => item.id === activeTab)?.label}
              </h2>
              <div className="content-breadcrumb">
                Admin Dashboard / {navigationItems.find(item => item.id === activeTab)?.label}
              </div>
            </div>
            
            <div className="admin-content-body">
              {/* Subscription status card — shown on the dashboard tab */}
              {activeTab === 'dashboard' && adminSubscriptionStatus && (
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: '14px 16px',
                  marginBottom: 20,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: isMobile ? 12 : 24,
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  minHeight: 'auto',
                  flexShrink: 0,
                  boxSizing: 'border-box',
                  overflow: 'visible',
                }}>
                  {/* Icon + title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 22 }}>🏷️</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Admin Subscription
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                        {adminSubscriptionStatus.planName || 'No Plan'}
                      </div>
                    </div>
                  </div>
                  {/* Status badge */}
                  <span style={{
                    padding: '4px 14px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 700,
                    background:
                      adminSubscriptionStatus.status === 'ACTIVE' ? '#d1fae5' :
                      adminSubscriptionStatus.status === 'PLATFORM_OWNER' ? '#dbeafe' :
                      adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '#fef9c3' :
                      adminSubscriptionStatus.status === 'SUSPENDED' ? '#ffedd5' :
                      adminSubscriptionStatus.status === 'EXPIRED' ? '#fee2e2' : '#f3f4f6',
                    color:
                      adminSubscriptionStatus.status === 'ACTIVE' ? '#065f46' :
                      adminSubscriptionStatus.status === 'PLATFORM_OWNER' ? '#1d4ed8' :
                      adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '#92400e' :
                      adminSubscriptionStatus.status === 'SUSPENDED' ? '#9a3412' :
                      adminSubscriptionStatus.status === 'EXPIRED' ? '#991b1b' : '#6b7280',
                  }}>
                    {adminSubscriptionStatus.status === 'PLATFORM_OWNER' ? '✓ Platform Owner – Unlimited' :
                     adminSubscriptionStatus.status === 'ACTIVE' ? '✓ Active' :
                     adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '⚠ Grace Period' :
                     adminSubscriptionStatus.status === 'SUSPENDED' ? '⛔ Suspended' :
                     adminSubscriptionStatus.status === 'EXPIRED' ? '✕ Expired' :
                     adminSubscriptionStatus.status === 'NONE' ? 'No Subscription' : adminSubscriptionStatus.status}
                  </span>
                  {/* Expiry */}
                  {adminSubscriptionStatus.expiresAt && adminSubscriptionStatus.status !== 'PLATFORM_OWNER' && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Expires
                      </span>
                      <span style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: adminSubscriptionStatus.status === 'EXPIRED' ? '#991b1b' :
                               adminSubscriptionStatus.status === 'GRACE_PERIOD' ? '#92400e' : '#111827'
                      }}>
                        {new Date(adminSubscriptionStatus.expiresAt).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {/* Grace Period End */}
                  {adminSubscriptionStatus.graceEndsAt && adminSubscriptionStatus.status === 'GRACE_PERIOD' && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Grace ends
                      </span>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#92400e' }}>
                        {new Date(adminSubscriptionStatus.graceEndsAt).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {/* Plan features (for active plans) */}
                  {adminSubscriptionStatus.maxStrategies && adminSubscriptionStatus.status === 'ACTIVE' && (
                    <div style={{ display: 'flex', gap: 16, marginLeft: 'auto' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>STRATEGIES</div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                          {myStrategyCount !== null ? (
                            <span>
                              <span style={{ color: myStrategyCount >= adminSubscriptionStatus.maxStrategies ? '#dc2626' : '#111827' }}>
                                {myStrategyCount}
                              </span>
                              <span style={{ color: '#9ca3af' }}>/{adminSubscriptionStatus.maxStrategies}</span>
                            </span>
                          ) : (
                            <span style={{ color: '#111827' }}>{adminSubscriptionStatus.maxStrategies}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>RETAIL USERS</div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                          {enrolledUserCount !== null ? (
                            <span>
                              <span style={{ color: enrolledUserCount >= adminSubscriptionStatus.maxRetailUsers ? '#dc2626' : '#111827' }}>
                                {enrolledUserCount}
                              </span>
                              <span style={{ color: '#9ca3af' }}>/{adminSubscriptionStatus.maxRetailUsers}</span>
                            </span>
                          ) : (
                            <span style={{ color: '#111827' }}>{adminSubscriptionStatus.maxRetailUsers}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {renderContent}
            </div>
          </main>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobile && mobileMenuOpen && (
          <div 
            className="mobile-menu-overlay" 
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 998
            }}
          />
        )}
      </div>
      {createPortal(
        <nav className="mobile-bottom-nav">
          <div className="mobile-nav-container">
            {(() => {
              const filteredItems = navigationItems.filter(item => navItemVisible(item));
              const totalItems = filteredItems.length;
              // Show up to 4 items if there are more than 5 total (to leave room for More button)
              // Otherwise show all items (up to 5) and add logout directly
              const maxItemsToShow = totalItems > 5 ? 4 : 5;
              const itemsToShow = filteredItems.slice(0, maxItemsToShow);
              
              return (
                <>
                  {itemsToShow.map(item => (
                    <button
                      key={item.id}
                      className={`mobile-nav-item ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => {
                        if (item.id === 'tradingBotsLink') {
                          navigate('/admin/trading-bots');
                        } else if (item.id === 'kyt') {
                          navigate('/admin/kyt');
                        } else {
                          handleTabChange(item.id);
                        }
                      }}
                    >
                      <div className="mobile-nav-icon">{getTabIcon(item.id)}</div>
                      <div className="mobile-nav-label">{item.label}</div>
                    </button>
                  ))}
                  {totalItems > 5 && (
                    <button
                      className="mobile-nav-item"
                      onClick={() => setShowBottomSheet(true)}
                    >
                      <div className="mobile-nav-icon"><FaEllipsisH style={{ color: '#e7038e', fontSize: '22px' }} /></div>
                      <div className="mobile-nav-label">More</div>
                    </button>
                  )}
                  {/* Always show logout if there are 5 or fewer items, otherwise it's in the bottom sheet */}
                  {totalItems <= 5 && (
                    <button
                      className="mobile-nav-item logout-item"
                      onClick={handleLogout}
                      style={{ color: '#dc2626' }}
                    >
                      <div className="mobile-nav-icon"><FaSignOutAlt style={{ color: '#dc2626', fontSize: '22px' }} /></div>
                      <div className="mobile-nav-label">Logout</div>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </nav>,
        document.body
      )}
      {showBottomSheet && createPortal(
        <div className="mobile-bottom-sheet-overlay" onClick={() => setShowBottomSheet(false)}>
          <div className="mobile-bottom-sheet" onClick={e => e.stopPropagation()}>
            <div 
              className="sheet-handle" 
              onClick={(e) => {
                e.stopPropagation(); // Prevent bubbling to sheet
                setShowBottomSheet(false);
              }}
              style={{ cursor: 'pointer' }}
            />
            <div className="sheet-nav-content">
              {(() => {
                const filteredForSheet = navigationItems.filter(item => navItemVisible(item));
                const total = filteredForSheet.length;
                const maxInBar = total > 5 ? 4 : 5;
                const overflowItems = filteredForSheet.slice(maxInBar);
                return overflowItems.map(item => (
                  <button
                    key={item.id}
                    className="sheet-nav-item"
                    onClick={() => {
                      if (item.id === 'tradingBotsLink') {
                        setShowBottomSheet(false);
                        navigate('/admin/trading-bots');
                        return;
                      }
                      if (item.id === 'kyt') {
                        setShowBottomSheet(false);
                        handleTabChange('kyt');
                        return;
                      }
                      handleTabChange(item.id);
                      setShowBottomSheet(false);
                    }}
                  >
                    <span className="sheet-nav-icon">{getTabIcon(item.id)}</span>
                    <span className="sheet-nav-label">{item.label}</span>
                  </button>
                ));
              })()}
              {/* Logout option - always show at the bottom */}
              <button
                className="sheet-nav-item logout-item"
                onClick={() => {
                  setShowBottomSheet(false);
                  handleLogout();
                }}
                style={{
                  borderTop: '2px solid #e5e7eb',
                  marginTop: '8px',
                  paddingTop: '16px',
                  color: '#dc2626'
                }}
              >
                <span className="sheet-nav-icon"><FaSignOutAlt style={{ color: '#dc2626', fontSize: '20px' }} /></span>
                <span className="sheet-nav-label">Logout</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {renewModalOpen && createPortal(
        <div
          onClick={() => setRenewModalOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 12000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 12, padding: 18 }}
          >
            <h3 style={{ margin: 0, fontSize: 19, color: '#111827', fontFamily: 'Nunito, sans-serif' }}>
              Renew admin subscription
            </h3>
            <p style={{ margin: '8px 0 16px', fontSize: 13, color: '#6b7280' }}>
              Choose payment method. For crypto, select the network and symbol before starting.
            </p>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Payment method</label>
            <select
              value={renewPaymentMethod}
              onChange={(e) => setRenewPaymentMethod(e.target.value as 'STRIPE' | 'CRYPTO')}
              style={{ width: '100%', marginTop: 6, marginBottom: 12, padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            >
              {adminRenewAllowedPaymentMethods.includes('STRIPE') && <option value="STRIPE">Stripe</option>}
              {adminRenewAllowedPaymentMethods.includes('CRYPTO') && <option value="CRYPTO">Crypto</option>}
            </select>
            {renewPaymentMethod === 'CRYPTO' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Network</label>
                  <select
                    value={renewCryptoBlockchain}
                    onChange={(e) => setRenewCryptoBlockchain(e.target.value)}
                    style={{ width: '100%', marginTop: 6, padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
                  >
                    <option value="ethereum">Ethereum</option>
                    <option value="polygon">Polygon</option>
                    <option value="bitcoin">Bitcoin</option>
                    <option value="stellar">Stellar</option>
                    <option value="solana">Solana</option>
                    <option value="cardano">Cardano</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Symbol</label>
                  <input
                    value={renewCryptoSymbol}
                    onChange={(e) => setRenewCryptoSymbol(e.target.value.toUpperCase())}
                    placeholder="USDT"
                    style={{ width: '100%', marginTop: 6, padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>
            )}
            {renewError && (
              <div style={{ marginBottom: 8, color: '#b91c1c', fontSize: 13 }}>{renewError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setRenewModalOpen(false)}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void startAdminRenewalFlow({
                  paymentMethod: renewPaymentMethod,
                  cryptoBlockchain: renewPaymentMethod === 'CRYPTO' ? renewCryptoBlockchain : undefined,
                  cryptoSymbol: renewPaymentMethod === 'CRYPTO' ? renewCryptoSymbol : undefined,
                })}
                disabled={renewBusy}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#086afb', color: '#fff', cursor: renewBusy ? 'wait' : 'pointer' }}
              >
                {renewBusy ? 'Starting...' : 'Start renewal'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AdminDashboard; 